import { motion } from "framer-motion";
import { ArrowUpRight, Compass, ShieldCheck, Anchor, Handshake } from "lucide-react";

const chapters = [
  {
    n: "01",
    title: "Considered execution.",
    body:
      "We treat every engagement — from procurement to interior fit-out — as an act of craftsmanship. Details are engineered, not improvised.",
  },
  {
    n: "02",
    title: "End-to-end capability.",
    body:
      "Procurement, supply, interior design, project execution and business support — coordinated under one accountable partner, so nothing slips through the seams.",
  },
  {
    n: "03",
    title: "Quiet reliability.",
    body:
      "We prefer competence over claims. Documented processes, transparent communication and delivery windows that we honour.",
  },
  {
    n: "04",
    title: "Built for the long view.",
    body:
      "Our relationships outlast individual orders. Nsoul Ventures is designed to be the partner you return to — for the next site, the next season, the next chapter.",
  },
];

const values = [
  { icon: Compass, title: "Precision", body: "Engineered scope, tight specifications and disciplined execution — every time." },
  { icon: ShieldCheck, title: "Accountability", body: "A single owner for every engagement. Clear ownership from first call to handover." },
  { icon: Anchor, title: "Reliability", body: "Delivery windows we honour. Documented processes and honest tradeoffs." },
  { icon: Handshake, title: "Long-Term Partnership", body: "We invest in relationships that outlast individual orders and seasons." },
];

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.7, 0, 0.3, 1], delay: i * 0.06 },
  }),
};

export default function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative bg-[var(--nv-paper)] text-[var(--nv-navy)] py-28 md:py-40 overflow-hidden"
    >
      {/* Blueprint decorative layer — carries language from the Hero */}
      <AboutBlueprintLayer />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-6 items-end">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={reveal}
            className="col-span-12 md:col-span-4"
          >
            <div className="overline text-[var(--nv-gold)] flex items-center gap-3" data-testid="about-overline">
              <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
              <span>About — Manifesto</span>
            </div>
          </motion.div>
          <motion.h2
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={reveal}
            className="col-span-12 md:col-span-8 display text-5xl md:text-7xl lg:text-[5.5rem]"
            data-testid="about-heading"
          >
            A quieter kind of <span className="italic text-[var(--nv-gold)]">excellence</span>, built section by section.
          </motion.h2>
        </div>

        {/* Chapters — alternating rhythm */}
        <div className="mt-20 md:mt-32 space-y-16 md:space-y-24">
          {chapters.map((c, i) => {
            const flip = i % 2 === 1; // alternate
            return (
              <motion.article
                key={c.n}
                custom={i}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
                variants={reveal}
                className="grid grid-cols-12 gap-6 items-start border-t border-[var(--nv-line)] pt-10 md:pt-14"
                data-testid={`about-chapter-${c.n}`}
              >
                {!flip ? (
                  <>
                    <div className="col-span-12 md:col-span-3">
                      <div className="font-serif italic text-[8rem] md:text-[10rem] lg:text-[13rem] leading-none number-stroke text-[var(--nv-navy)]">
                        {c.n}
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:col-start-5">
                      <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">
                        {c.title}
                      </h3>
                      <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[var(--nv-navy)]/70 max-w-xl">
                        {c.body}
                      </p>
                    </div>
                    <div className="hidden md:block col-span-2 col-start-11 text-right">
                      <div className="font-mono text-xs tracking-[0.24em] uppercase text-[var(--nv-navy)]/50">Chapter</div>
                      <div className="font-serif italic text-2xl mt-1 text-[var(--nv-gold)]">{c.n}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="hidden md:block col-span-2 col-start-1">
                      <div className="font-mono text-xs tracking-[0.24em] uppercase text-[var(--nv-navy)]/50">Chapter</div>
                      <div className="font-serif italic text-2xl mt-1 text-[var(--nv-gold)]">{c.n}</div>
                    </div>
                    <div className="col-span-12 md:col-span-6 md:col-start-4">
                      <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">
                        {c.title}
                      </h3>
                      <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[var(--nv-navy)]/70 max-w-xl">
                        {c.body}
                      </p>
                    </div>
                    <div className="col-span-12 md:col-span-3 md:col-start-10 md:text-right">
                      <div className="font-serif italic text-[8rem] md:text-[10rem] lg:text-[13rem] leading-none number-stroke text-[var(--nv-navy)]">
                        {c.n}
                      </div>
                    </div>
                  </>
                )}
              </motion.article>
            );
          })}
        </div>

        {/* Why Nsoul Ventures — values mini grid */}
        <div className="mt-28 md:mt-40" data-testid="about-values">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.4 }}
            variants={reveal}
            className="grid grid-cols-12 gap-6 items-end mb-10 md:mb-14"
          >
            <div className="col-span-12 md:col-span-6">
              <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
                <span>Why Nsoul Ventures</span>
              </div>
              <h3 className="mt-6 font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05]" data-testid="values-heading">
                Four principles that <span className="italic text-[var(--nv-gold)]">shape our work.</span>
              </h3>
            </div>
          </motion.div>

          <div className="grid grid-cols-12 gap-4 md:gap-6">
            {values.map((v, i) => {
              const Icon = v.icon;
              return (
                <motion.article
                  key={v.title}
                  custom={i}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.3 }}
                  variants={reveal}
                  className="col-span-12 sm:col-span-6 lg:col-span-3 group border-t border-[var(--nv-navy)] pt-8"
                  data-testid={`value-card-${v.title.toLowerCase().replace(/\s/g, '-')}`}
                >
                  <div className="flex items-start justify-between mb-10">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-full border border-[var(--nv-navy)]/30 group-hover:border-[var(--nv-gold)] transition-colors duration-500">
                      <Icon className="w-4 h-4" strokeWidth={1.6} />
                    </span>
                    <span className="font-mono text-[10px] tracking-[0.24em] text-[var(--nv-navy)]/45">0{i + 1}</span>
                  </div>
                  <h4 className="font-serif text-2xl md:text-3xl tracking-tight leading-[1.1]">{v.title}</h4>
                  <p className="mt-4 text-sm leading-relaxed text-[var(--nv-navy)]/70">{v.body}</p>
                </motion.article>
              );
            })}
          </div>
        </div>

        {/* Closing CTA */}
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          variants={reveal}
          className="mt-28 md:mt-40 relative border-t border-[var(--nv-line)] pt-16 md:pt-24"
          data-testid="about-closing-cta"
        >
          <div className="grid grid-cols-12 gap-6 items-end">
            <div className="col-span-12 md:col-span-8">
              <div className="overline text-[var(--nv-gold)] flex items-center gap-3">
                <span className="inline-block w-6 h-px bg-[var(--nv-gold)]" />
                <span>Next</span>
              </div>
              <h3
                className="mt-6 display text-4xl md:text-6xl lg:text-7xl leading-[0.98]"
                data-testid="about-cta-heading"
              >
                Let&apos;s build something <span className="italic text-[var(--nv-gold)]">that lasts.</span>
              </h3>
              <p className="mt-8 text-[15px] md:text-base leading-relaxed text-[var(--nv-navy)]/70 max-w-2xl">
                Whether you require procurement, project execution, interior design, or complete
                business solutions — we&apos;re ready to deliver with precision and professionalism.
              </p>
            </div>
            <div className="col-span-12 md:col-span-4 md:justify-self-end flex flex-wrap gap-3">
              <a href="#contact" className="btn-gold" data-testid="about-cta-primary">
                Get in Touch <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
              <a href="#solutions" className="btn-ghost" data-testid="about-cta-secondary">
                Explore Solutions
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/**
 * Blueprint decorative layer — low-opacity technical marks that carry
 * the Hero's visual language into About.
 */
function AboutBlueprintLayer() {
  return (
    <div className="absolute inset-0 pointer-events-none" aria-hidden>
      <svg
        viewBox="0 0 1600 2400"
        preserveAspectRatio="xMidYMid slice"
        className="absolute inset-0 w-full h-full opacity-[0.11]"
      >
        <defs>
          <pattern id="about-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
            <path d="M 60 0 L 0 0 0 60" fill="none" stroke="#0A192F" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#about-grid)" />

        {/* technical circles + measurement marks */}
        <g stroke="#0A192F" strokeWidth="0.7" fill="none">
          <circle cx="1400" cy="380" r="240" strokeDasharray="2 6" />
          <circle cx="1400" cy="380" r="160" />
          <circle cx="1400" cy="380" r="4" fill="#C5A059" stroke="none" />
          <line x1="1400" y1="140" x2="1400" y2="620" strokeDasharray="1 4" />
          <line x1="1160" y1="380" x2="1640" y2="380" strokeDasharray="1 4" />

          <circle cx="240" cy="1400" r="180" />
          <circle cx="240" cy="1400" r="90" strokeDasharray="2 6" />
          <circle cx="240" cy="1400" r="3" fill="#C5A059" stroke="none" />

          <path d="M 1200 1600 L 1360 1520 L 1520 1600 L 1520 1760 L 1360 1840 L 1200 1760 Z" />
          <path d="M 1360 1520 L 1360 1680 L 1520 1760" />
          <path d="M 1360 1680 L 1200 1600" />
        </g>

        {/* small gold accent points */}
        <g fill="#C5A059">
          <circle cx="420" cy="240" r="3" />
          <circle cx="1240" cy="1120" r="3" />
          <circle cx="820" cy="1980" r="3" />
          <circle cx="60" cy="820" r="3" />
        </g>

        {/* Dimension labels */}
        <g fontFamily="'JetBrains Mono', ui-monospace, monospace" fill="#0A192F" fontSize="10" letterSpacing="0.22em">
          <text x="1400" y="120">NV — 02</text>
          <text x="60" y="1180">CH · MANIFESTO</text>
          <text x="1200" y="1500">FIG · B</text>
        </g>
      </svg>
    </div>
  );
}
